import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    Home,
    TrendingUp,
    Shield,
    MapPin,
    DollarSign,
    Calendar,
    Users,
    CheckCircle,
    ArrowRight,
    Star,
    Building2,
    Target,
    Award,
    Heart,
    Sparkles,
    Eye,
    Bookmark,
    MessageCircle,
    Globe,
    Clock,
    BarChart3,
    PieChart,
    Loader2,
    AlertCircle,
    ChevronDown,
    ChevronUp
} from 'lucide-react';

import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import ProjectCard from '../../components/projects/ProjectCard';
import { useGetPublicProjectsQuery } from '../../store/slices/apiSlice';
import { usePageMetadata } from '../../hooks/usePageMetadata';

const RealEstateInvestmentNigeria = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);

    // Set page metadata and canonical URL
    usePageMetadata('realEstateNigeria');

    // Fetch projects from API
    const { data: projectsResponse, isLoading: loading, error: projectsError } = useGetPublicProjectsQuery();
    const allProjects = Array.isArray(projectsResponse?.data) ? projectsResponse?.data :
        Array.isArray(projectsResponse?.data?.projects) ? projectsResponse?.data?.projects : [];

    // Filter for real estate projects
    const projects = allProjects.filter(project =>
        project.category.name.toLowerCase().includes('real estate') ||
        project.category.name.toLowerCase().includes('residential') ||
        project.category.name.toLowerCase().includes('commercial')
    );

    const error = projectsError ? 'Failed to load real estate projects. Please try again later.' : null;

    useEffect(() => {
        setIsVisible(true);
    }, []);

    // Key Features
    const features = [
        {
            icon: MapPin,
            title: "Prime Locations",
            description: "Properties in Lagos, Abuja, and other high-growth Nigerian cities"
        },
        {
            icon: TrendingUp,
            title: "High ROI",
            description: "Average returns of 15-25% annually with strong appreciation potential"
        },
        {
            icon: Shield,
            title: "Legal Protection",
            description: "All investments backed by proper legal documentation and title deeds"
        },
        {
            icon: Users,
            title: "Expert Management",
            description: "Professional property management and tenant screening services"
        }
    ];

    // Helper function to format currency
    const formatCurrency = (amount: number, currencySymbol: string = '$') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0
        }).format(amount).replace('$', currencySymbol);
    };

    // Calculate real stats from real estate projects
    const calculateStats = () => {
        if (projects.length === 0) {
            return {
                averageROI: "0%",
                averageDuration: "0 months",
                minInvestment: "$0",
                projectCount: 0
            };
        }

        const averageROI = projects.reduce((sum, project) => sum + parseFloat(project.expected_roi_percentage), 0) / projects.length;
        const averageDuration = projects.reduce((sum, project) => sum + project.roi_duration_months, 0) / projects.length;
        const minInvestment = Math.min(...projects.map(project => parseFloat(project.rules.min_allowed_investment)));

        return {
            averageROI: `${averageROI.toFixed(1)}%`,
            averageDuration: `${averageDuration.toFixed(0)} months`,
            minInvestment: formatCurrency(minInvestment),
            projectCount: projects.length
        };
    };

    const stats = calculateStats();

    // Property Types
    const propertyTypes = [
        {
            title: "Luxury Apartments",
            description: "High-end residential properties in prime locations",
            price: "$50,000 - $200,000",
            roi: "18-25%",
            image: "🏢"
        },
        {
            title: "Commercial Spaces",
            description: "Office buildings and retail spaces",
            price: "$100,000 - $500,000",
            roi: "20-30%",
            image: "🏬"
        },
        {
            title: "Residential Houses",
            description: "Single-family homes and duplexes",
            price: "$30,000 - $150,000",
            roi: "15-22%",
            image: "🏠"
        },
        {
            title: "Land Development",
            description: "Raw land for development projects",
            price: "$10,000 - $100,000",
            roi: "25-40%",
            image: "🏗️"
        }
    ];

    // Investment Process
    const processSteps = [
        {
            step: "01",
            title: "Choose Property",
            description: "Browse our curated selection of verified properties"
        },
        {
            step: "02",
            title: "Legal Review",
            description: "Our legal team reviews all documentation"
        },
        {
            step: "03",
            title: "Investment",
            description: "Complete your investment with secure payment"
        },
        {
            step: "04",
            title: "Management",
            description: "We handle property management and tenant relations"
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />

            {/* Hero Section */}
            <section className="relative pt-20 pb-16 bg-white">
                <div className="container mx-auto px-6 relative z-10">
                    <div className={`text-center max-w-4xl mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                        <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                            <Home className="w-4 h-4" />
                            Premium Real Estate Investment
                        </div>

                        <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                            Nigeria's Premier
                            <span className="text-blue-600"> Real Estate Market</span>
                        </h1>

                        <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                            Access Nigeria's most lucrative real estate opportunities in Lagos, Abuja, and Port Harcourt.
                            Invest in premium properties with guaranteed returns, expert management, and transparent processes.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300 hover:-translate-y-1">
                                Invest Now
                                <ArrowRight className="w-5 h-5" />
                            </button>
                            <button className="inline-flex items-center gap-2 bg-white text-gray-700 px-8 py-4 rounded-xl font-semibold border border-gray-200 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                                <Eye className="w-5 h-5" />
                                Browse Properties
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Why Invest in Nigerian Real Estate?
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Nigeria's real estate market offers exceptional opportunities for investors seeking high returns and portfolio diversification.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {features.map((feature, index) => (
                            <div
                                key={index}
                                className={`bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                                style={{ transitionDelay: `${index * 100}ms` }}
                            >
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-6">
                                    <feature.icon className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Property Types Section */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Available Property Types
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Choose from our diverse portfolio of verified properties across Nigeria's major cities.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {propertyTypes.map((property, index) => (
                            <div
                                key={index}
                                className={`bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden group ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                                style={{ transitionDelay: `${index * 100}ms` }}
                            >
                                <div className="h-48 bg-blue-50 flex items-center justify-center text-6xl">
                                    {property.image}
                                </div>

                                <div className="p-6">
                                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-300">
                                        {property.title}
                                    </h3>
                                    <p className="text-gray-600 mb-4 leading-relaxed">
                                        {property.description}
                                    </p>

                                    <div className="space-y-2 mb-6">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-500">Price Range:</span>
                                            <span className="font-semibold text-gray-900">{property.price}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-500">Expected ROI:</span>
                                            <span className="font-semibold text-green-600">{property.roi}</span>
                                        </div>
                                    </div>

                                    <button className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                                        View Details
                                        <ArrowRight className="w-4 h-4 inline ml-2" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Real Estate Projects Section */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Available Real Estate Projects
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Explore our curated selection of real estate investment opportunities in Nigeria.
                        </p>
                    </div>

                    {loading ? (
                        <div className="flex justify-center items-center py-20">
                            <div className="flex items-center gap-3">
                                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                                <span className="text-lg text-gray-600">Loading real estate projects...</span>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="flex justify-center items-center py-20">
                            <div className="text-center">
                                <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">Failed to Load Projects</h3>
                                <p className="text-gray-600 mb-4">{error}</p>
                                <button
                                    onClick={() => window.location.reload()}
                                    className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors duration-300"
                                >
                                    Try Again
                                </button>
                            </div>
                        </div>
                    ) : projects.length === 0 ? (
                        <div className="text-center py-20">
                            <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Real Estate Projects Available</h3>
                            <p className="text-gray-600">Check back later for new real estate investment opportunities.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {projects.map((project, index) => (
                                <ProjectCard
                                    key={project.project_id}
                                    project={project}
                                    className={`transition-all duration-1000 delay-${index * 100} ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Investment Process Section */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Simple Investment Process
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Our streamlined process makes real estate investment simple and secure.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {processSteps.map((step, index) => (
                            <div
                                key={index}
                                className={`text-center transition-all duration-1000 delay-${index * 100} ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                            >
                                <div className="relative mb-8">
                                    <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full text-white text-2xl font-bold mb-4">
                                        {step.step}
                                    </div>
                                    {index < processSteps.length - 1 && (
                                        <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-blue-600 to-purple-600 -translate-x-1/2"></div>
                                    )}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{step.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
                <div className="container mx-auto px-6 text-center">
                    <div className="max-w-3xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                            Ready to Invest in Nigerian Real Estate?
                        </h2>
                        <p className="text-xl text-blue-100 mb-8">
                            Join thousands of successful investors who have built wealth through Nigerian real estate investments.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                                Start Your Investment
                                <ArrowRight className="w-5 h-5" />
                            </button>
                            <Link
                                to="/contact"
                                className="inline-flex items-center gap-2 bg-transparent text-white px-8 py-4 rounded-xl font-semibold border-2 border-white hover:bg-white hover:text-blue-600 transition-all duration-300 hover:-translate-y-1"
                            >
                                <MessageCircle className="w-5 h-5" />
                                Contact Us
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Learn More About Real Estate Investment in Nigeria Section */}
            <section className="bg-white py-12 px-4 border-t border-gray-200">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-8">
                        <button
                            onClick={() => setIsLearnMoreOpen(!isLearnMoreOpen)}
                            className="flex items-center justify-center gap-2 mx-auto bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors duration-300 border border-gray-300"
                            aria-expanded={isLearnMoreOpen}
                            aria-controls="real-estate-content"
                        >
                            <span className="font-medium">
                                {isLearnMoreOpen ? 'Hide Learn More About Real Estate Investment in Nigeria' : 'Learn More About Real Estate Investment in Nigeria'}
                            </span>
                            {isLearnMoreOpen ? (
                                <ChevronUp className="w-4 h-4 text-gray-600" />
                            ) : (
                                <ChevronDown className="w-4 h-4 text-gray-600" />
                            )}
                        </button>
                    </div>

                    <div
                        id="real-estate-content"
                        className={`overflow-hidden transition-all duration-500 ease-in-out ${isLearnMoreOpen ? 'max-h-none opacity-100' : 'max-h-0 opacity-0'
                            }`}
                    >
                        <article className="prose prose-lg max-w-none">
                            {/* Main Content Section */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                                    Introduction: Investment in Real Estate in Nigeria as an Alternative to Wealth Generation
                                </h2>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Nigeria, the largest economy in Africa, is also one of the most attractive real estate markets. With a population of more than 220 million and rapid urban growth, the need for housing, offices, and infrastructure has never been greater.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    For both local and foreign investors in the diaspora, investing in real estate in Nigeria provides a great opportunity to earn steady rental income, long-term property appreciation, as well as help the nation's economy grow.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Many Nigerians who live abroad have supported their families for years by sending money back home. Remittances are a band-aid and not wealth creation. But real estate investment offers diaspora Nigerians such an opportunity to invest in a physical asset, protect their financial future, and stay culturally linked to home.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Now, Diasporawise investments is making it easy, safe, and transparent to invest in the populous country's mushrooming real estate market.
                                </p>
                            </div>

                            {/* Section 2 */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">The Increasing Significance of Real Estate Investment in Nigeria</h2>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Addressing the Housing Deficit</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Nigeria has a housing deficit in excess of 17 million units to contend with. Cities like Lagos, Abuja, and Port Harcourt are growing at breakneck speed, swelling with new arrivals every year. This ensures stable opportunities for property investment in Nigeria, which is one of the most lucrative options.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">High Return on Investment (ROI)</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    The rental yields in prime Nigerian cities are some of the highest on the African continent. Investors benefit from: Plentiful rental income from residential and commercial tenants. Appreciation in value as cities grow and land becomes more valuable.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Diverse Opportunities Across Segments</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Real estate in Nigeria is not just apartment buildings and penthouses. Investors can choose from: Affordable housing estates. Luxury apartments. Retail and office developments. Land banking opportunities. Properties for small-budget investors.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Government Incentives for Diaspora Investors</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    The Nigerian Government recently endorsed the idea of early diaspora real estate investment. Projects and policies are geared towards luring diaspora investments to infrastructural, housing, and commercial projects.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Transparency Through Digital Platforms</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    The days when diaspora Nigerians relied solely on their relatives to close property deals are long gone. Organisations like Diasporawise carry out due diligence, digital title verification, and project updates — which means Nigerians buying property from overseas can do so more safely than ever before.
                                </p>
                            </div>

                            {/* Section 3 */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Crucial Sections of Real Estate Investment in Nigeria</h2>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Residential Property Investment Nigeria</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    The housing market continues to be the best-performing sector for real estate investors. Opportunities include: Gated communities. Apartment complexes. Short-let apartments (frequented by Lagos professionals and travelers). Homes for Nigeria's expanding middle class.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Residential investment provides constant rental returns, as well as helps in solving Nigeria's housing problem.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Commercial Property Investment Nigeria</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Nigeria's commercial cities are offering exciting prospects to investors looking for higher yields. Lagos, Abuja, and Port Harcourt are trading hubs where demand for retail and office space is increasing.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Key projects include: Office towers and business hubs. Retail centers and shopping malls. Mixed-use developments including residential, retail, and leisure.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Commercial projects usually provide more ROI compared to residential ones but also require higher capital.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Land Investment</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Today, land banking (buying up land in strategically located fast-growing areas) is still the number one investment consideration for Nigerian investors. Increased urban sprawl forces development to spread out into new areas where property values quickly rise.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    "Land speculation is less risky and does not require that much oversight, so it becomes an attractive investment option for diaspora Nigerians."
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Fractional Property Investment Nigeria</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Fractional ownership is a good point of entry for diaspora investors on smaller budgets. It enables you to invest in high-quality projects through co-investments with other investors.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Benefits include: Lower financial barrier. Direct access to quality real estate deals. Shared rental income and appreciation.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    One of the biggest trends in diaspora real estate Nigeria is fractional investing, which is helping to democratise investment in property.
                                </p>
                            </div>

                            {/* Section 4 */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Advantages of Investing in Nigeria Property</h2>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Creating Wealth</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Constructing assets in the form of finance for long term beyond remittance.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Predictable Returns</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    You simply cannot go wrong on rental returns.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Cultural Connection</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Investing in property is a way for diaspora Nigerians to stay connected with their homeland.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Nation Building</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    All investments generate jobs, housing, and infrastructure.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Legacy Planning</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Real estate can be passed on as intergenerational wealth.
                                </p>
                            </div>

                            {/* Section 5 */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Steps to Begin Your Real Estate Investment in Nigeria</h2>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">What Are You Trying to Accomplish?</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Are you looking for rental income, capital gains, or fractional entrance?
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Select a Trusted Platform</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Ventures such as Diasporawise which offer investments in real estate that are safe and trusted.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Choose What Type of Investment</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Residential, commercial, land, or fractional.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Check Legal Papers</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    All land titles and clearances in place or not.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Payment Security</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Make payment through secure digital payment systems.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Follow Your Investment</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Online dashboards have changed everything.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Build Your Wealth</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    See your assets grow and support the development of Nigeria.
                                </p>
                            </div>

                            {/* Conclusion */}
                            <div className="mb-8">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Conclusion: Real Estate Investment Nigeria (the Future)</h2>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Investing in real estate Nigeria is much more than an investment—it's an opportunity to build wealth, culture, and country.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Property investment is not a high-risk or unstructured proposition for diaspora Nigerians any more. The likes of Diasporawise on-boarded platforms now give investors and those in the diaspora safe and transparent investment opportunities for residential, commercial, or fractional projects.
                                </p>
                                <p className="text-gray-700 leading-relaxed">
                                    Whether you are new to investing or you're growing your portfolio, real estate investment in Nigeria is time-tested and proven to be one of the best ways to build wealth and financial freedom for you and your future generations.
                                </p>
                            </div>
                        </article>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default RealEstateInvestmentNigeria;
